
interface MarkdownTextProps {
  text: string;
}

interface TextPart {
  type: 'text' | 'bold' | 'italic';
  content: string;
}

export default function MarkdownText({ text }: MarkdownTextProps) {
  // Handle empty, null, or undefined text
  if (!text || typeof text !== 'string') {
    return <span>Conteúdo não disponível</span>;
  }

  // Clean and normalize the text first
  const cleanText = text
    .replace(/\\n/g, '\n')
    .replace(/\\"/g, '"')
    .replace(/\\'/g, "'")
    .replace(/\\\\/g, '\\')
    .trim();

  // Simple markdown parser for **bold** and *italic*
  const parseText = (inputText: string): TextPart[] => {
    const parts: TextPart[] = [];
    let currentIndex = 0;
    
    // Regex to find **bold** and *italic* patterns (non-greedy)
    const markdownRegex = /(\*\*[^*]+?\*\*|\*[^*]+?\*)/g;
    let match;
    
    while ((match = markdownRegex.exec(inputText)) !== null) {
      // Add text before the match
      if (match.index > currentIndex) {
        const beforeText = inputText.slice(currentIndex, match.index);
        if (beforeText) {
          parts.push({
            type: 'text',
            content: beforeText
          });
        }
      }
      
      // Add the formatted text
      const matchedText = match[0];
      if (matchedText.startsWith('**') && matchedText.endsWith('**') && matchedText.length > 4) {
        parts.push({
          type: 'bold',
          content: matchedText.slice(2, -2)
        });
      } else if (matchedText.startsWith('*') && matchedText.endsWith('*') && matchedText.length > 2) {
        parts.push({
          type: 'italic',
          content: matchedText.slice(1, -1)
        });
      } else {
        // Fallback to plain text if pattern doesn't match expected format
        parts.push({
          type: 'text',
          content: matchedText
        });
      }
      
      currentIndex = match.index + matchedText.length;
    }
    
    // Add remaining text
    if (currentIndex < inputText.length) {
      const remainingText = inputText.slice(currentIndex);
      if (remainingText) {
        parts.push({
          type: 'text',
          content: remainingText
        });
      }
    }
    
    // If no markdown found, return the whole text as plain text
    if (parts.length === 0) {
      parts.push({
        type: 'text',
        content: inputText
      });
    }
    
    return parts;
  };

  const parts = parseText(cleanText);
  
  return (
    <span>
      {parts.map((part, index) => {
        switch (part.type) {
          case 'bold':
            return <strong key={index} className="font-bold text-gray-800">{part.content}</strong>;
          case 'italic':
            return <em key={index} className="italic text-gray-700">{part.content}</em>;
          default:
            return <span key={index}>{part.content}</span>;
        }
      })}
    </span>
  );
}
